import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, ShieldCheck, Award, ExternalLink, Filter } from "lucide-react";
import Seal from "@/components/Seal";

export default function Registry() {
  const [q, setQ] = useState("");
  const [organization, setOrganization] = useState("ALL");
  const [credType, setCredType] = useState("ALL");
  const [state, setState] = useState("");
  const [status, setStatus] = useState("active");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchResults = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status };
      if (q) params.q = q;
      if (organization !== "ALL") params.organization = organization;
      if (credType !== "ALL") params.credential_type = credType;
      if (state) params.state = state;
      const { data } = await api.get("/registry/search", { params });
      setResults(data);
    } finally {
      setLoading(false);
    }
  }, [q, organization, credType, state, status]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  return (
    <div data-testid="registry-page">
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-3">Public credential registry</div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-primary tracking-tight">
            Find a credentialed practitioner.
          </h1>
          <p className="mt-3 max-w-2xl text-slate-600">
            Search the official registry of ACIS and IBTHM credential holders. Results reflect the
            current credential status as recorded by the boards.
          </p>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="p-5 border-slate-200 shadow-sm mb-6">
            <div className="grid md:grid-cols-12 gap-3">
              <div className="md:col-span-4 relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="registry-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && fetchResults()}
                  placeholder="Name or credential number"
                  className="pl-9"
                />
              </div>
              <div className="md:col-span-2">
                <Select value={organization} onValueChange={setOrganization}>
                  <SelectTrigger data-testid="registry-org-select"><SelectValue placeholder="Board" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All boards</SelectItem>
                    <SelectItem value="ACIS">ACIS</SelectItem>
                    <SelectItem value="IBTHM">IBTHM</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-3">
                <Select value={credType} onValueChange={setCredType}>
                  <SelectTrigger data-testid="registry-type-select"><SelectValue placeholder="Credential type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All credential types</SelectItem>
                    <SelectItem value="ACIS-PA">ACIS-PA · Practitioner</SelectItem>
                    <SelectItem value="IBTHM-CT">IBTHM-CT · Certified Trichologist</SelectItem>
                    <SelectItem value="IBTHM-Holistic">IBTHM-Holistic · Holistic Medicine</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Input
                  data-testid="registry-state-input"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  placeholder="State (e.g. NY)"
                />
              </div>
              <div className="md:col-span-1">
                <Button data-testid="registry-search-btn" onClick={fetchResults} className="w-full bg-primary text-white hover:bg-[#113A64]">
                  Search
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-3 text-xs text-slate-500">
              <Filter className="h-3.5 w-3.5" />
              <span>Status:</span>
              {["active", "suspended", "revoked", "expired"].map((s) => (
                <button
                  key={s}
                  onClick={() => setStatus(s)}
                  className={`px-2 py-0.5 rounded-full font-medium capitalize ${
                    status === s ? "bg-primary text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                  data-testid={`registry-status-${s}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </Card>

          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-slate-600">
              {loading ? "Searching…" : <><span className="font-semibold text-primary">{results.length}</span> credential{results.length === 1 ? "" : "s"} found</>}
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {results.map((c) => (
              <Card key={c.id} className="p-5 border-slate-200 shadow-sm hover:shadow-md transition-all" data-testid={`registry-result-${c.credential_number}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3">
                    <Seal credentialType={c.credential_type} size="sm" />
                    <div>
                      <div className="font-heading font-semibold text-primary text-lg">{c.holder_name}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{c.credential_type}{c.state ? ` · ${c.state}` : ""}</div>
                    </div>
                  </div>
                  <StatusBadge status={c.status} />
                </div>
                <div className="border-t border-slate-200 pt-3 grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Credential #</div>
                    <div className="font-mono-data text-sm text-primary mt-0.5">{c.credential_number}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Board</div>
                    <div className="text-sm font-semibold text-primary mt-0.5">{c.organization}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Issued</div>
                    <div className="text-sm text-slate-700 mt-0.5">{formatDate(c.issue_date)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Expires</div>
                    <div className="text-sm text-slate-700 mt-0.5">{formatDate(c.expiry_date)}</div>
                  </div>
                </div>
                {c.school?.id && (
                  <div className="mt-3 rounded-md bg-slate-50 border border-slate-200 p-2.5">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Graduating institution</div>
                    <div className="text-xs text-slate-700 mt-0.5 flex items-center gap-1.5">
                      <ShieldCheck className="h-3 w-3 text-emerald-600 shrink-0" />
                      <span className="truncate">{c.school.name}</span>
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200 capitalize text-[10px] ml-auto">
                        {c.school.accreditation_status}
                      </Badge>
                    </div>
                  </div>
                )}
                <Link to={`/verify/${c.credential_number}`} className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
                  Verify this credential <ExternalLink className="h-3.5 w-3.5" />
                </Link>
              </Card>
            ))}
            {!loading && results.length === 0 && (
              <div className="col-span-full">
                <Card className="p-10 text-center border-dashed" data-testid="registry-empty">
                  <Award className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <div className="font-heading font-semibold text-primary">
                    {q || state || credType !== "ALL" || organization !== "ALL" || status !== "active"
                      ? "No credentials match your search"
                      : "Registry is currently empty"}
                  </div>
                  <div className="text-sm text-slate-500 mt-2 max-w-xl mx-auto">
                    {q || state || credType !== "ALL" || organization !== "ALL" || status !== "active"
                      ? "Try removing filters or searching by a credential number."
                      : "The IBTHM Registry is currently accepting applications for board certification and professional licensure. Approved practitioners will appear in the registry following successful credential issuance."}
                  </div>
                </Card>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    active: "bg-emerald-100 text-emerald-800 border-emerald-200",
    suspended: "bg-amber-100 text-amber-800 border-amber-200",
    revoked: "bg-red-100 text-red-800 border-red-200",
    expired: "bg-slate-100 text-slate-700 border-slate-200",
  };
  return (
    <Badge variant="outline" className={`capitalize ${map[status] || map.expired}`}>
      {status === "active" && <ShieldCheck className="h-3 w-3 mr-1" />}
      {status}
    </Badge>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso.slice(0, 10);
  }
}
